import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { RootState } from '.';
import api from '../api/BlancoClient';
import i18n from '../i18n';
import { isBeforeDailyProcessing, isToday } from '../lib/birthday';
import { sendException } from '../lib/google-analytics';
import { getFruitById } from '../models/Fruit';
import { LandProfile } from '../models/LandProfile';
import { PassportInfo } from '../models/PassportInfo';
import { Resident, ResidentItem } from '../models/Resident';
import { UserProfile } from '../models/UserProfile';
import { actions as userActions, getFreshToken, UserState } from './user';
import { checkErrorType, Progress } from './utils/helper';

export type PassportErrorType =
  | 'player-profile-not-found'
  | 'player-not-in-island'
  | 'loading-failed'
  | 'unstable-network'
  | 'unknown';

export interface PassportState {
  info?: PassportInfo;
  residents?: ResidentItem[];
  status?: Progress;
  errorType?: PassportErrorType;
}

const initialState: PassportState = {};

const slice = createSlice({
  name: 'passport',
  initialState,
  reducers: {
    getPassport: state => {
      state.status = 'loading';
      state.errorType = undefined;
    },

    getPassportSucceeded: (
      state,
      { payload }: PayloadAction<{ passport: PassportInfo; residents: ResidentItem[] }>
    ) => {
      state.info = payload.passport;
      state.residents = payload.residents;
      state.status = 'succeeded';
    },

    getPassportFailed: (state, { payload }: PayloadAction<PassportErrorType>) => {
      state.status = 'failed';
      state.errorType = payload;
    },

    reset: state => {
      state.status = undefined;
      state.errorType = undefined;
      state.info = undefined;
      state.residents = undefined;
    }
  }
});

export default slice.reducer;

const { getPassport, reset, ...internalActions } = slice.actions;

export const actions = {
  getPassport,
  reset
};

const { getPassportSucceeded, getPassportFailed } = internalActions;

export function* passportSaga() {
  yield takeLatest(getPassport.type, getPassportSaga);
}

function* getPassportSaga() {
  const userState: UserState = yield select((state: RootState) => state.user);
  const { selectedUserId, users } = userState;
  const selectedUser = selectedUserId && users.find(u => u.id === selectedUserId);
  if (!selectedUser) {
    sendException('selected user not found in passport saga', true);
    throw new Error('selected user not found in passport saga');
  }

  const name = selectedUser.name;
  const landName = selectedUser.land.name;
  const landDisplayId = selectedUser.land.displayId;
  let profileImage = selectedUser.image;

  try {
    const baid: string = yield call(getFreshToken);
    const language = i18n.language;

    const [userProfile, landProfile]: [UserProfile, LandProfile] = yield all([
      call(api.getUserProfile, selectedUser.id, language, baid),
      call(api.getLandProfile, selectedUser.land.id, language, baid)
    ]);

    profileImage = userProfile.mJpeg || profileImage;
    const handleName = userProfile.mHandleName;
    const birthday = userProfile.mBirth;
    const registrationDate = userProfile.mTimeStamp;
    const comment = userProfile.mComment;
    const fruit = getFruitById(landProfile.mFruit.id);
    const fruitString = landProfile.mFruit.name;
    const isLandMaster = userProfile.mIsLandMaster;
    const myDesignAuthorId = userProfile.mMyDesignAuthorId;
    const dreamAddress = userProfile.mDreamAddress;
    const resortPlannerId = userProfile.mResortPlannerId;

    const isBirthday = isToday(birthday) && !isBeforeDailyProcessing(new Date());

    const passport: PassportInfo = {
      name,
      landName,
      landDisplayId,
      profileImage,
      fruit,
      fruitString,
      handleName,
      birthday,
      registrationDate,
      comment,
      isBirthday,
      isLandMaster,
      myDesignAuthorId,
      dreamAddress,
      resortPlannerId
    };
    const residents = convertToResidents(landProfile, selectedUser.id);

    yield put(getPassportSucceeded({ passport, residents }));
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getPassportFailed('unstable-network'));
    } else {
      if (error.response.data?.code) {
        const { code } = error.response.data;
        const passportErrorType = getPassportError(code);
        yield put(getPassportFailed(passportErrorType));
      } else {
        yield put(getPassportFailed('loading-failed'));
      }
    }
  }
}

function convertToResidents(landProfile: LandProfile, selectedUserId: string): ResidentItem[] {
  const villager: Resident[] = landProfile.mVillager
    .filter(v => !(v.userId && v.userId === selectedUserId))
    .map(v => {
      const isReported = v.screening === 'ng';
      let birthday = undefined;
      let isBirthday = undefined;
      if (v.mBirthMonth && v.mBirthDay) {
        birthday = {
          month: v.mBirthMonth,
          day: v.mBirthDay
        };
        isBirthday = isToday(birthday) && !isBeforeDailyProcessing(new Date());
      }
      return {
        name: v.mPNm,
        image: v.mJpeg,
        birthday,
        isBirthday,
        isReported,
        isPlayer: true,
        isLandMaster: v.mIsLandMaster
      };
    });
  const npc: Resident[] = landProfile.mNormalNpc.map(v => ({
    name: v.name,
    image: v.image,
    birthday: { month: v.birthMonth, day: v.birthDay },
    isBirthday:
      isToday({ month: v.birthMonth, day: v.birthDay }) && !isBeforeDailyProcessing(new Date())
  }));
  return [...villager, ...npc].map((r, index) => ({ ...r, index }));
}

function getPassportError(code?: string) {
  switch (code) {
    case '1102':
      return 'player-profile-not-found';
    case '1401':
      return 'player-not-in-island';
    default:
      return 'unknown';
  }
}
